/**
 * TrustKeyFacts — what the deed actually says about who gets what.
 *
 * Beno asked for this at the 1 August 2026 meeting
 * (`tasks/beno-meeting-20260801/IMPLEMENTATION-PLAN.html`, wave 4, "Key facts"):
 * surface "whether the trust mandates equal distribution among children and
 * grandchildren or is fully discretionary, shown to trustees so they can
 * administer it properly and to beneficiaries so they know where they stand."
 *
 * His governing rule, in writing the same day: "Ek sal dit uitlos as dit nie in
 * die deed is nie. As dit in die deed is, dan kan dit wys." — leave it out if it
 * is not in the deed; show it when the deed says it. So this component asserts
 * NOTHING it cannot back with the deed's own words, and it says so plainly when
 * it cannot. It is deliberately the least confident component in the app.
 *
 * Three states, and the difference between the last two matters:
 *   1. Determined  — the deed says it, and we can show the sentence that says it.
 *   2. Read, silent — a deed was read and it does not settle the question.
 *   3. Nothing on file — no reading exists (no deed processed, or a trust that
 *      predates this feature). We must NOT report that as "the deed is silent",
 *      because we have not read a deed to know.
 *
 * NOT the same thing as `TrusteeDecisionRule` (majority / unanimous / quorum).
 * That is how trustees VOTE to approve a decision; this is how beneficiaries
 * receive money. The two are easy to conflate and this component deliberately
 * never mentions voting.
 *
 * The prop types are re-declared here rather than imported: the field does not
 * exist on the shared `TrustDoc` yet (see `backend/src/lib/build-trust-doc.ts`),
 * so there is nothing in `lib/api` to import. Fold these into the API types when
 * `TrustDoc.deedDistribution` lands.
 */
import { useId } from 'react';

export type DeedDistributionBasis =
  | 'equal'
  | 'fixed-shares'
  | 'discretionary'
  | 'not-determined';

export interface DeedDistributionFact {
  basis: DeedDistributionBasis;
  /** The deed's own words justifying `basis`. Null when nothing was quotable. */
  quote: string | null;
  /** Where the quote sits in the deed, e.g. "clause 12.3". Null when unlabelled. */
  clauseReference: string | null;
}

/** The three bases we are willing to assert. Anything else — 'not-determined',
 *  or a value from an API that has drifted ahead of this build — abstains. */
const DETERMINED_BASES = ['equal', 'fixed-shares', 'discretionary'] as const;

export type DeterminedBasis = (typeof DETERMINED_BASES)[number];

function isDeterminedBasis(v: string): v is DeterminedBasis {
  return (DETERMINED_BASES as readonly string[]).includes(v);
}

/**
 * A discriminated union rather than a flat record, on purpose: it makes
 * "determined with no quote" IMPOSSIBLE TO REPRESENT, so the rule cannot be
 * broken by a later edit that forgets it. `determined: true` structurally
 * carries a non-empty `quote` and a basis we actually recognise.
 */
export type ResolvedDistribution =
  | {
      determined: true;
      /** A determined finding always came from a deed. */
      readFromDeed: true;
      basis: DeterminedBasis;
      quote: string;
      clauseReference: string | null;
    }
  | {
      determined: false;
      /** True when a deed reading exists at all, including an explicitly silent
       *  one. Separates "the deed does not say" from "we have not read a deed". */
      readFromDeed: boolean;
      basis: 'not-determined';
      quote: null;
      clauseReference: null;
    };

const NOT_DETERMINED = {
  determined: false as const,
  basis: 'not-determined' as const,
  quote: null,
  clauseReference: null,
};

function trimmedOrNull(v: string | null | undefined): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}

/**
 * Decide what may honestly be shown. Exported because this is the load-bearing
 * half — the rendering is presentation, this is the rule — and it should be
 * pinnable in tests directly.
 *
 * The backend already applies the same rule at extraction and again at the
 * persistence boundary (`normalizeDeedDistribution`). Repeating it here is not
 * redundant: this component may be handed a value from a cache written before
 * that rule existed, from a hand-edited record, or from an API that has since
 * drifted. The claim is checked at the moment it would be made to a human.
 */
export function resolveDistributionFact(
  fact: DeedDistributionFact | null | undefined,
): ResolvedDistribution {
  if (!fact) return { ...NOT_DETERMINED, readFromDeed: false };

  const basis = trimmedOrNull(fact.basis);
  // Covers 'not-determined' and any unrecognised value from a drifted API.
  if (!basis || !isDeterminedBasis(basis)) return { ...NOT_DETERMINED, readFromDeed: true };

  const quote = trimmedOrNull(fact.quote);
  // A basis with nothing to quote is a guess. On downgrade the clause reference
  // goes too — citing "clause 12.3" beside "not determined" would imply we found
  // a clause we cannot stand behind.
  if (!quote) return { ...NOT_DETERMINED, readFromDeed: true };

  return {
    determined: true,
    readFromDeed: true,
    basis,
    quote,
    clauseReference: trimmedOrNull(fact.clauseReference),
  };
}

/** Plain-language reading of each basis. Written for a beneficiary who has never
 *  read a trust deed, not for a fiduciary specialist. */
const BASIS_COPY: Record<
  'equal' | 'fixed-shares' | 'discretionary',
  { headline: string; body: string }
> = {
  equal: {
    headline: 'Equal shares',
    body:
      'The deed directs that the beneficiaries share equally. A beneficiary’s share does not depend on a trustee decision.',
  },
  'fixed-shares': {
    headline: 'Fixed shares set out in the deed',
    body:
      'The deed sets the portion each beneficiary receives. The trustees do not decide how it is split.',
  },
  discretionary: {
    headline: 'At the trustees’ discretion',
    body:
      'The trustees decide who receives income or capital, when, and how much. A beneficiary is entitled to no particular amount until the trustees make an award.',
  },
};


export interface TrustKeyFactsProps {
  /** The deed reading held for this trust. `undefined`/`null` = nothing on file. */
  deedDistribution?: DeedDistributionFact | null;
}

export function TrustKeyFacts({ deedDistribution }: TrustKeyFactsProps) {
  const headingId = useId();
  const fact = resolveDistributionFact(deedDistribution);

  return (
    <section
      data-testid="trust-key-facts"
      aria-labelledby={headingId}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--hairline)',
        borderRadius: 'var(--r-md, 8px)',
        padding: '18px 20px',
      }}
    >
      <h3
        id={headingId}
        style={{
          fontFamily: 'var(--serif)',
          fontSize: 17,
          fontWeight: 500,
          color: 'var(--ink)',
          margin: '0 0 14px',
        }}
      >
        Key facts
      </h3>

      <div className="eyebrow mute">How benefits are shared</div>

      {fact.determined ? (
        <div data-testid="distribution-determined" style={{ marginTop: 8 }}>
          {/* Own testid: the deed's own words often repeat the headline phrase
              ("…in equal shares"), so a plain text query cannot tell the claim
              from the quote. Tests must assert on the claim. */}
          <div
            data-testid="distribution-headline"
            style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--ink)', marginBottom: 4 }}
          >
            {BASIS_COPY[fact.basis].headline}
          </div>
          <p style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink-2)', margin: '0 0 14px' }}>
            {BASIS_COPY[fact.basis].body}
          </p>

          {/* Attribution first, then the words. The reader should know this is
              the deed talking before they read the sentence — the whole point is
              that Elreign is not the one making the claim. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 6,
            }}
          >
            <span className="eyebrow mute">From the trust deed</span>
            {fact.clauseReference && (
              <span
                data-testid="distribution-clause"
                style={{ fontSize: 11.5, color: 'var(--ink-3)' }}
              >
                {fact.clauseReference}
              </span>
            )}
          </div>
          <blockquote
            data-testid="distribution-quote"
            style={{
              margin: 0,
              padding: '10px 14px',
              borderLeft: '2px solid var(--accent-400)',
              background: 'var(--surface-2)',
              fontFamily: 'var(--serif)',
              fontSize: 13.5,
              lineHeight: 1.6,
              color: 'var(--ink-2)',
            }}
          >
            {`“${fact.quote}”`}
          </blockquote>
        </div>
      ) : (
        <div data-testid="distribution-undetermined" style={{ marginTop: 8 }}>
          <div style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--ink-3)', marginBottom: 4 }}>
            Not determined
          </div>
          {/* Two different truths, deliberately worded differently. Reporting
              "the deed does not say" when no deed has been read would itself be
              a claim we cannot support. */}
          <p style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink-2)', margin: 0 }}>
            {fact.readFromDeed
              ? 'The trust deed on file does not say how benefits are to be shared between the beneficiaries. The deed itself remains the authority. Read it before acting on this.'
              : 'Nothing on file records how benefits are to be shared between the beneficiaries. Read the trust deed itself before relying on this.'}
          </p>
        </div>
      )}
    </section>
  );
}
